import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/contexts/I18nContext';
import { AlertTriangle, Shield } from 'lucide-react';

interface MfaEnableModalProps {
  visible: boolean;
  onOk: () => void;
  onCancel: () => void;
  isSocialLogin: boolean;
}

export const MfaEnableModal: React.FC<MfaEnableModalProps> = ({
  visible,
  onOk,
  onCancel,
  isSocialLogin,
}) => {
  const { t } = useTranslation();

  if (isSocialLogin) {
    return (
      <Dialog open={visible} onOpenChange={(open) => !open && onCancel()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <DialogTitle>{t('security.mfaNotAvailable')}</DialogTitle>
            </div>
          </DialogHeader>
          
          <div className="py-4">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="p-3 bg-amber-50 rounded-full">
                <AlertTriangle className="h-8 w-8 text-amber-500" />
              </div>
              
              <div className="space-y-2">
                <p className="font-medium">{t('security.mfaNotAvailableTitle')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('security.mfaNotAvailableDesc')}
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onCancel}>
              {t('common.understood')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={visible} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-500" />
            <DialogTitle>{t('security.enableMfa')}</DialogTitle>
          </div>
          <DialogDescription>
            {t('security.mfaModalDescription')}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <div className="flex flex-col items-center text-center space-y-4">
            <div className="p-3 bg-blue-50 rounded-full">
              <Shield className="h-8 w-8 text-blue-500" />
            </div>
            
            <div className="space-y-2">
              <h3 className="font-medium">{t('security.mfaWillProvide')}</h3>
              <ul className="text-sm text-muted-foreground space-y-1 text-left">
                <li>• {t('security.mfaBenefit1')}</li>
                <li>• {t('security.mfaBenefit2')}</li>
                <li>• {t('security.mfaBenefit3')}</li>
              </ul>
            </div>
            
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-800">
                {t('security.mfaWarning')}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button onClick={onOk}>
            {t('security.enableMfa')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
